import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { MapPin } from "lucide-react";
import { Button } from "./ui/button";
import RunClubDashboard from "./RunClubDashboard";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import RunRegistrationForm from "./RunRegistrationForm";
import { useFetch } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { transformMeetings, transformClubs, transformMeetingToRunCard, matchesTimeFilter } from "../utils/dataTransformers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { getFeaturedClubs, getLocationForApi, getClubsListParams, expandQueryToMetroCities, expandLatLngToMetroCities } from "../utils/locationUtils";
import { DISCOVER_MEETINGS_PARAMS, HOMEPAGE_CLUBS_LIMIT } from "../constants/api";
import MeetingCard from "./ui/MeetingCard";
import ClubCard from "./ui/ClubCard";
import CardMarquee from "./ui/CardMarquee";
import HeroSection from "./ui/HeroSection";
import HowItWorksSection from "./ui/HowItWorksSection";
import AppGallerySection from "./ui/AppGallerySection";
import ForRunClubsSection from "./ui/ForRunClubsSection";
import Reveal from "./ui/Reveal";
import CarouselNav, { CarouselSideArrows, getCarouselMaxIndex, CAROUSEL_CARD_WIDTH_PCT, CAROUSEL_CARDS_DESKTOP } from "./ui/CarouselNav";
import { useCarouselSwipe } from "../hooks/useCarouselSwipe";
import ScrollableCardModal from "./ui/ScrollableCardModal";
import DownloadAppPrompt from "./ui/DownloadAppPrompt";
import { openInAppOrFallback } from "../utils/deepLink";
import { toast } from "sonner";
import { CLUB_TYPES } from "../constants/clubConstants";

const GUEST_LOCATION_STORAGE_KEYS = {
  LAT: "runcards_guest_lat",
  LON: "runcards_guest_lon",
  DENIED: "runcards_guest_location_denied",
  SKIPPED: "runcards_guest_location_skipped",
};

function readGuestLocationFromSession() {
  if (typeof sessionStorage === "undefined") return null;
  if (sessionStorage.getItem(GUEST_LOCATION_STORAGE_KEYS.DENIED)) return "denied";
  if (sessionStorage.getItem(GUEST_LOCATION_STORAGE_KEYS.SKIPPED)) return "skipped";
  const lat = sessionStorage.getItem(GUEST_LOCATION_STORAGE_KEYS.LAT);
  const lon = sessionStorage.getItem(GUEST_LOCATION_STORAGE_KEYS.LON);
  if (lat != null && lon != null) {
    const la = parseFloat(lat);
    const lo = parseFloat(lon);
    if (!Number.isNaN(la) && !Number.isNaN(lo)) return { lat: la, lon: lo };
  }
  return null;
}

const HomePage = () => {
  const [heroSearchQuery, setHeroSearchQuery] = useState("");
  const [searchCity, setSearchCity] = useState("");
  // City explicitly submitted from the hero search. Drives a server-side
  // re-fetch of runs + clubs for that city instead of the default location-based
  // "nearby" fetch. Empty string = show nearby.
  const [submittedCity, setSubmittedCity] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [hasSetDefaultLocation, setHasSetDefaultLocation] = useState(false);
  const [guestLocation, setGuestLocation] = useState(readGuestLocationFromSession);
  const [locationPromptLoading, setLocationPromptLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const hasRequestedGuestLocationRef = useRef(false);
  const runsSectionRef = useRef(null);
  // Set when a hero search fires; the effect below scrolls to the results once
  // the refetch settles (scrolling mid-refetch gets interrupted by re-renders).
  const pendingSearchScrollRef = useRef(false);

  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Hide logo when scrolling down, show when near top (like original nav)
  useEffect(() => {
    const handleScroll = () => {
      setIsLogoVisible(window.scrollY <= 100);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-request browser location for anonymous users so the native permission dialog appears
  useEffect(() => {
    if (loading || user || guestLocation !== null) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    if (hasRequestedGuestLocationRef.current) return;
    hasRequestedGuestLocationRef.current = true;
    setLocationPromptLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(GUEST_LOCATION_STORAGE_KEYS.LAT, String(latitude));
          sessionStorage.setItem(GUEST_LOCATION_STORAGE_KEYS.LON, String(longitude));
          sessionStorage.removeItem(GUEST_LOCATION_STORAGE_KEYS.DENIED);
          sessionStorage.removeItem(GUEST_LOCATION_STORAGE_KEYS.SKIPPED);
        }
        setGuestLocation({ lat: latitude, lon: longitude });
        setLocationPromptLoading(false);
      },
      (err) => {
        setLocationPromptLoading(false);
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(GUEST_LOCATION_STORAGE_KEYS.DENIED, "1");
        }
        setGuestLocation("denied");
        if (err.code === 1) {
          toast.error("Location access was denied.");
        } else {
          toast.error("Location unavailable. Try searching by city instead.");
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [loading, user, guestLocation]);
  
  // Handle Strava OAuth callback
  useEffect(() => {
    const handleStravaCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      
      if (error) {
        toast.error(`Strava authorization failed: ${error}`);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      if (code) {
        try {
          const response = await apiService.strava.connect(code);
          
          if (response) {
            toast.success('Strava account connected successfully!');
            // Redirect to profile page
            navigate('/profile');
          } else {
            toast.error('No response from Strava connect API');
          }
        } catch (error) {
          
          // Show more specific error message
          if (error.response?.data?.detail?.includes('invalid')) {
            toast.error('Authorization code is invalid. Please try connecting again.');
          } else {
            toast.error('Failed to connect Strava account');
          }
        }
        
        // Clean up URL immediately to prevent re-execution
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    
    // Only run if there's a code parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('code') || urlParams.get('error')) {
      handleStravaCallback();
    }
  }, []); // Empty dependency array to run only once
  
  // Carousel state
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [currentClubsCarouselIndex, setCurrentClubsCarouselIndex] = useState(0);
  const [selectedRun, setSelectedRun] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  // Card tap → try to open the app at that run/club; if it isn't installed
  // (or we're on desktop) show the download prompt instead. The prompt also
  // carries the deep link so its primary button can retry the app open.
  const [appPrompt, setAppPrompt] = useState({ open: false, label: "", type: null, id: null });
  const closeAppPrompt = () => setAppPrompt((p) => ({ ...p, open: false }));
  const handleCardOpen = useCallback(({ type, id, label }) => {
    openInAppOrFallback({
      type,
      id,
      onFallback: () => setAppPrompt({ open: true, label: label || "", type, id }),
    });
  }, []);
  const [isMobile, setIsMobile] = useState(false);
  
  // Use constants for club categories, matching icons from CLUB_TYPES
  const clubCategories = CLUB_TYPES.map(type => ({
    name: type.value,
    icon: type.icon,
    count: 0 // Will be populated from actual data
  }));

  const timeFilters = ["Next 7 days", "Today", "Tomorrow", "Next week"];
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("Next 7 days");

  const location = useMemo(
    () => getLocationForApi(user, guestLocation),
    [user?.latitude, user?.longitude, guestLocation]
  );

  const clubsParams = useMemo(
    () => getClubsListParams({ limit: HOMEPAGE_CLUBS_LIMIT, location, userCity: user?.city }),
    [location, user?.city]
  );

  const fetchHomepageData = useCallback(async () => {
    const city = submittedCity.trim();
    // For known metros (e.g. "Miami" → all South-FL cities) the backend's
    // substring `city` filter is too narrow — it'd miss Brickell, Coral
    // Gables, Doral, etc. In that case drop the server-side city filter,
    // fetch wider, and let the client-side metro filter take over.
    const metroCities = city ? expandQueryToMetroCities(city) : [];
    const isMetroSearch = metroCities.length > 1;
    const meetingsLimit = city ? 500 : 500;
    const clubsLimit = city ? 500 : 500;

    // When the user has location and isn't searching a city, ask the backend
    // for runs within 50 mi only — otherwise distance-sorted "near me" can
    // bleed in runs from Philly/NYC when the local area is sparse.
    const nearbyParams = location
      ? { ...DISCOVER_MEETINGS_PARAMS, limit: meetingsLimit, within_miles: 50 }
      : { ...DISCOVER_MEETINGS_PARAMS, limit: meetingsLimit };

    const [meetingsData, clubsData] = await Promise.all([
      apiService.meetings.getAll(
        city
          ? isMetroSearch
            ? { limit: meetingsLimit, status: "upcoming" }
            : { limit: meetingsLimit, status: "upcoming", city }
          : nearbyParams,
        city ? null : location
      ),
      apiService.clubs.getAll(
        city
          ? { limit: clubsLimit, user_city: city, with_meetings: true }
          : { ...clubsParams, limit: clubsLimit, with_meetings: true }
      ),
    ]);
    return {
      meetings: meetingsData ?? [],
      clubs: clubsData ?? [],
    };
  }, [location, clubsParams, submittedCity]);

  const { data: homepageData, loading: homepageLoading, refetch: refetchHomepage } = useFetch(
    fetchHomepageData,
    [fetchHomepageData],
    { enabled: !user || user.user_type !== "runclub" }
  );

  // Transform data using our utilities
  const meetings = useMemo(() => {
    if (!homepageData?.meetings) return [];
    return transformMeetings(homepageData.meetings);
  }, [homepageData?.meetings]);

  const clubs = useMemo(() => {
    if (!homepageData?.clubs) return [];
    // Match the mobile Club screen: closest first. The backend already returns
    // clubs distance-sorted (with a same-city tiebreaker); re-assert it here so
    // the featured slice below keeps the nearest clubs.
    return [...transformClubs(homepageData.clubs)].sort((a, b) => {
      const da = a.distance_from_user != null ? a.distance_from_user : Number.POSITIVE_INFINITY;
      const db = b.distance_from_user != null ? b.distance_from_user : Number.POSITIVE_INFINITY;
      return da - db;
    });
  }, [homepageData?.clubs]);

  // Filter and sort meetings using our utility (same as mobile RunScreen: default Next 7 days, user can change via dropdown)
  // When a city has been searched, narrow to the metro's sibling cities
  // (e.g. searching Miami → keep Brickell, Coral Gables, Doral runs too).
  // When no city is searched but the user has location, derive the metro from
  // their coords so neighborhood-level cities still show.
  const metroCitiesForFilter = useMemo(() => {
    if (submittedCity.trim()) {
      return new Set(expandQueryToMetroCities(submittedCity));
    }
    if (location) {
      const cities = expandLatLngToMetroCities(location.latitude, location.longitude);
      return new Set(cities);
    }
    return new Set();
  }, [submittedCity, location]);

  const runEvents = useMemo(() => {
    const upcomingMeetings = meetings.filter(meeting => {
      if ((meeting.status?.toLowerCase?.() ?? '') !== 'upcoming') return false;
      if (metroCitiesForFilter.size > 0) {
        const mc = (meeting.city || '').toLowerCase().trim();
        if (!mc || !metroCitiesForFilter.has(mc)) return false;
      }
      if (selectedTimeFilter !== 'All') {
        if (!meeting.scheduled_date) return false;
        return matchesTimeFilter(meeting.scheduled_date, selectedTimeFilter);
      }
      return true;
    });

    // Match the mobile Run screen (sortMeetingsByScheduledTime): soonest
    // upcoming first, then closest as a tiebreaker — most relevant runs lead.
    upcomingMeetings.sort((a, b) => {
      const ta = a.scheduled_date ? new Date(a.scheduled_date).getTime() : NaN;
      const tb = b.scheduled_date ? new Date(b.scheduled_date).getTime() : NaN;
      const va = Number.isFinite(ta) ? ta : Number.POSITIVE_INFINITY;
      const vb = Number.isFinite(tb) ? tb : Number.POSITIVE_INFINITY;
      if (va !== vb) return va - vb;
      const da = a.distance_from_user != null ? a.distance_from_user : Number.POSITIVE_INFINITY;
      const db = b.distance_from_user != null ? b.distance_from_user : Number.POSITIVE_INFINITY;
      return da - db;
    });

    return upcomingMeetings.map((meeting) => {
      return transformMeetingToRunCard(meeting);
    });
  }, [meetings, selectedTimeFilter, metroCitiesForFilter]);

  const extendedRunEvents = React.useMemo(() => runEvents, [runEvents]);

  // Featured clubs - defined early so swipe hook can use length
  const [featuredClubs, setFeaturedClubs] = useState([]);
  const getFeaturedClubsData = React.useCallback(() => {
    // When we have a metro context (from search OR from user location),
    // filter the candidates to that metro's sibling cities so neighborhoods
    // like Brickell / Coral Gables / Doral all show alongside Miami.
    if (metroCitiesForFilter.size > 0) {
      return clubs
        .filter((c) => c.city && metroCitiesForFilter.has(c.city.toLowerCase().trim()))
        .slice(0, 50);
    }
    return getFeaturedClubs(clubs, searchCity, user?.city, 50);
  }, [metroCitiesForFilter, searchCity, user?.city, clubs]);
  React.useEffect(() => {
    if (!homepageLoading) setFeaturedClubs(getFeaturedClubsData());
  }, [getFeaturedClubsData, homepageLoading, clubs]);

  // After a hero search refetch completes, scroll the freshly-filtered sections
  // into view (deferred from handleHeroSearch so the layout is settled and the
  // smooth scroll isn't interrupted by the loading-state re-render).
  React.useEffect(() => {
    if (homepageLoading || !pendingSearchScrollRef.current) return;
    pendingSearchScrollRef.current = false;
    const el = runsSectionRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Fallback: some environments (and reduced-motion) don't animate smooth
    // scrollIntoView — if nothing moved, jump instantly so the results show.
    const startY = window.scrollY;
    setTimeout(() => {
      if (Math.abs(window.scrollY - startY) < 4) el.scrollIntoView({ block: "start" });
    }, 500);
  }, [homepageLoading]);

  const runsSwipe = useCarouselSwipe(extendedRunEvents.length, isMobile, setCurrentCarouselIndex);
  const clubsSwipe = useCarouselSwipe(featuredClubs.length, isMobile, setCurrentClubsCarouselIndex);

  // Carousel window: only render visible + adjacent items to limit DOM and image requests
  const runsWindowSize = isMobile ? 3 : CAROUSEL_CARDS_DESKTOP + 2;
  const clubsWindowSize = isMobile ? 3 : CAROUSEL_CARDS_DESKTOP + 2;
  const runsWindow = useMemo(
    () =>
      extendedRunEvents.slice(
        currentCarouselIndex,
        currentCarouselIndex + runsWindowSize
      ),
    [extendedRunEvents, currentCarouselIndex, runsWindowSize]
  );
  const clubsWindow = useMemo(
    () =>
      featuredClubs.slice(
        currentClubsCarouselIndex,
        currentClubsCarouselIndex + clubsWindowSize
      ),
    [featuredClubs, currentClubsCarouselIndex, clubsWindowSize]
  );

  // Clamp carousel indices when data changes (e.g. time filter reduces runs)
  React.useEffect(() => {
    const runsMax = getCarouselMaxIndex(extendedRunEvents.length, isMobile);
    setCurrentCarouselIndex((prev) => (prev > runsMax ? Math.max(0, runsMax) : prev));
  }, [extendedRunEvents.length, isMobile]);
  React.useEffect(() => {
    const clubsMax = getCarouselMaxIndex(featuredClubs.length, isMobile);
    setCurrentClubsCarouselIndex((prev) => (prev > clubsMax ? Math.max(0, clubsMax) : prev));
  }, [featuredClubs.length, isMobile]);

  // Popular cities for autocomplete
  const POPULAR_CITIES = [
    'New York, NY',
    'Los Angeles, CA',
    'Chicago, IL',
    'Houston, TX',
    'Phoenix, AZ',
    'Philadelphia, PA',
    'San Antonio, TX',
    'San Diego, CA',
    'Dallas, TX',
    'San Jose, CA',
    'Austin, TX',
    'Jacksonville, FL',
    'Fort Worth, TX',
    'Columbus, OH',
    'Charlotte, NC',
    'San Francisco, CA',
    'Indianapolis, IN',
    'Seattle, WA',
    'Denver, CO',
    'Washington, DC',
    'Boston, MA',
    'El Paso, TX',
    'Nashville, TN',
    'Detroit, MI',
    'Oklahoma City, OK',
    'Portland, OR',
    'Las Vegas, NV',
    'Memphis, TN',
    'Louisville, KY',
    'Baltimore, MD',
    'Milwaukee, WI',
    'Albuquerque, NM',
    'Tucson, AZ',
    'Fresno, CA',
    'Sacramento, CA',
    'Mesa, AZ',
    'Kansas City, MO',
    'Atlanta, GA',
    'Long Beach, CA',
    'Colorado Springs, CO',
    'Raleigh, NC',
    'Miami, FL',
    'Virginia Beach, VA',
    'Omaha, NE',
    'Oakland, CA',
    'Minneapolis, MN',
    'Tulsa, OK',
    'Arlington, TX',
    'Tampa, FL',
    'New Orleans, LA'
  ];

  const handleSearch = () => {
    if (searchCity.trim()) {
      navigate(`/runclubs?city=${encodeURIComponent(searchCity.trim())}`);
    }
  };

  // Set user's city as default search if available (only once on initial load)
  React.useEffect(() => {
    if (user?.city && !hasSetDefaultLocation) {
      setSearchCity(user.city);
      setHasSetDefaultLocation(true);
    }
  }, [user?.city, hasSetDefaultLocation]);

  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Set initial value
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Helper function to handle registration button clicks
  const handleRegistrationClick = (event, type = 'meeting') => {
    if (!user) {
      navigate('/auth');
    } else if (user.user_type === "runclub") {
      toast.error("Run club owners cannot register for runs. You can only create and manage runs for your club.");
    } else {
      // For authenticated users, open the meeting modal to view details
      setSelectedRun(event);
      setShowRunModal(true);
    }
  };

  // Generate suggestions based on search query
  const generateSuggestions = (query) => {
    if (!query || query.length < 2) return [];
    
    return POPULAR_CITIES.filter(city => 
      city.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 5); // Limit to 5 suggestions
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchCity(value);
    
    if (value.length >= 2) {
      const newSuggestions = generateSuggestions(value);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (city) => {
    setSearchCity(city);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchCity.length >= 2) {
      const newSuggestions = generateSuggestions(searchCity);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }
  };

  // Handle input blur (with delay to allow clicking on suggestions)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const showGuestLocationPrompt = !user && guestLocation === null;

  // When a city is explicitly searched, label the sections "in {City}" instead
  // of "nearby" so it's clear the search took effect.
  const cityLabel = submittedCity.trim() ? submittedCity.split(",")[0].trim() : "";

  const handleSkipLocation = useCallback(() => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(GUEST_LOCATION_STORAGE_KEYS.SKIPPED, "1");
    }
    setGuestLocation("skipped");
  }, []);

  // Manual re-request of the browser geolocation. Clears any prior deny/skip
  // and any searched city so the homepage drops back to "nearby" mode and
  // refetches runs + clubs against the fresh coordinates.
  const handleRefreshLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      toast.error("Location isn't available in this browser.");
      return;
    }
    setLocationPromptLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(GUEST_LOCATION_STORAGE_KEYS.LAT, String(latitude));
          sessionStorage.setItem(GUEST_LOCATION_STORAGE_KEYS.LON, String(longitude));
          sessionStorage.removeItem(GUEST_LOCATION_STORAGE_KEYS.DENIED);
          sessionStorage.removeItem(GUEST_LOCATION_STORAGE_KEYS.SKIPPED);
        }
        setGuestLocation({ lat: latitude, lon: longitude });
        setSubmittedCity("");
        setSearchCity("");
        setHeroSearchQuery("");
        setLocationPromptLoading(false);
        toast.success("Location updated — showing runs near you.");
      },
      (err) => {
        setLocationPromptLoading(false);
        if (err.code === 1) {
          toast.error("Location was blocked. Enable it in your browser settings.");
        } else {
          toast.error("Couldn't get your location. Try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // Hero search → filter in place. Re-fetch runs + clubs for this city (empty
  // resets to nearby) and scroll the freshly-filtered sections into view rather
  // than navigating to a separate page.
  const handleHeroSearch = useCallback((query) => {
    const c = (query || "").trim();
    setSubmittedCity(c);
    setSearchCity(c);
    setShowSuggestions(false);
    // Defer the scroll until the refetch settles (see effect below) so the
    // smooth scroll isn't interrupted by the loading-state re-render.
    pendingSearchScrollRef.current = true;
  }, []);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-runcards-background">
        <div className="text-runcards-text-primary">Loading...</div>
      </div>
    );
  }

  // Show run club dashboard if user is a run club
  if (user && user.user_type === "runclub") {
    // Show dashboard for run club users (even without club_id for now)
    // The dashboard will handle the case where club_id is null
    return <RunClubDashboard />;
  }

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary">
      <style>{`
        button:focus,
        a:focus,
        input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        button:focus-visible,
        a:focus-visible,
        input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>

      {/* Logo - fixed top left, vertically aligned with the Login button,
          hides on scroll (same transform as the Login wrapper). The source PNG
          is a padded 1024² canvas, so we crop to the wordmark via a background
          image (zoom + position) instead of object-contain. */}
      <Link
        to="/"
        className="fixed top-4 left-4 sm:top-6 sm:left-6 z-50"
        aria-label="Runcards home"
        style={{
          transform: isLogoVisible ? "translateY(0)" : "translateY(-200%)",
          opacity: isLogoVisible ? 1 : 0,
          pointerEvents: isLogoVisible ? "auto" : "none",
          transition: "transform 300ms ease, opacity 300ms ease",
        }}
      >
        {logoError ? (
          <span className="font-runcards-light text-lg text-white sm:text-xl">RunCards</span>
        ) : (
          <div
            role="img"
            aria-label="RunCards"
            className="h-9 w-56 sm:h-10 sm:w-60"
            style={{
              backgroundImage: "url('/RunCards_full_white_text_transparent.png')",
              backgroundRepeat: "no-repeat",
              backgroundSize: "auto 980%",
              backgroundPosition: "44% 45%",
            }}
          />
        )}
      </Link>

      {/* Login / Profile - fixed top right, hides on scroll (same as logo). */}
      <div
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50"
        style={{
          transform: isLogoVisible ? "translateY(0)" : "translateY(-200%)",
          opacity: isLogoVisible ? 1 : 0,
          pointerEvents: isLogoVisible ? "auto" : "none",
          transition: "transform 300ms ease, opacity 300ms ease",
        }}
      >
        {user ? (
          <Link
            to="/profile"
            className="rc-glass-dark inline-flex items-center justify-center px-4 py-2 rounded-runcards-lg font-runcards-bold text-runcards-sm hover:scale-[1.02] focus:outline-none"
          >
            Profile
          </Link>
        ) : (
          <Link
            to="/auth"
            className="rc-glass-dark inline-flex items-center justify-center px-4 py-2 rounded-runcards-lg font-runcards-bold text-runcards-sm hover:scale-[1.02] focus:outline-none"
          >
            Login
          </Link>
        )}
      </div>

      {/* Hero — video montage + search */}
      <HeroSection
        searchQuery={heroSearchQuery}
        onSearchQueryChange={setHeroSearchQuery}
        onSearch={handleHeroSearch}
        showGuestLocationPrompt={showGuestLocationPrompt}
        locationPromptLoading={locationPromptLoading}
        onSkipLocation={handleSkipLocation}
      />

      {/* Run Event Cards Carousel — header is contained, marquee is full-bleed */}
      <Reveal as="section" ref={runsSectionRef} className="relative scroll-mt-4 pt-8 sm:pt-10 md:pt-12 lg:pt-14 z-20 bg-runcards-background">
        {/* Section header — kept constrained for readability */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
          <div className="mb-5 sm:mb-7">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-xl">
                <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-runcards-accent">
                  What's on
                </p>
                <h2 className="font-runcards-black text-[clamp(2rem,5vw,3.25rem)] leading-[1.0] tracking-[-0.025em] text-runcards-text-primary">
                  {extendedRunEvents.length} {extendedRunEvents.length === 1 ? 'run' : 'runs'}
                  <span className="text-runcards-text-secondary">{cityLabel ? ` in ${cityLabel}` : ' nearby'}</span>
                </h2>
                <p className="mt-3 max-w-md font-runcards text-base text-runcards-text-secondary">
                  Group runs happening near you — pick one and just show up.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={handleRefreshLocation}
                  disabled={locationPromptLoading}
                  aria-label="Refresh location and show runs near me"
                  className="rc-glass-dark inline-flex items-center gap-2 rounded-runcards-lg px-3 py-2 font-runcards-bold text-runcards-sm disabled:opacity-50"
                >
                  <MapPin className={`h-4 w-4 ${locationPromptLoading ? "animate-pulse" : ""}`} />
                  <span className="hidden sm:inline">
                    {locationPromptLoading ? "Locating…" : "Near me"}
                  </span>
                </button>
                <Select value={selectedTimeFilter} onValueChange={setSelectedTimeFilter}>
                  <SelectTrigger className="rc-glass-dark w-[150px] shrink-0 border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeFilters.map((filter) => (
                      <SelectItem key={filter} value={filter}>
                        {filter}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee row spans the full viewport — no max-width, no side padding */}
        <CardMarquee
          items={extendedRunEvents.slice(0, 18)}
          renderItem={(event, index) => (
            <MeetingCard
              event={event}
              showHover={true}
              onClick={() => handleCardOpen({ type: "meeting", id: event.id, label: event.title || event.name || "this run" })}
              onButtonClick={() => handleCardOpen({ type: "meeting", id: event.id, label: event.title || event.name || "this run" })}
              buttonText="View Run"
              showPrimaryButton={false}
              priority={index === 0}
              useCardSize={true}
            />
          )}
        />
      </Reveal>

      {/* Featured Clubs Section — header contained, marquee full-bleed */}
      <Reveal as="section" className="pt-4 sm:pt-6 pb-8 sm:pb-12 bg-runcards-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
          <div className="mb-5 sm:mb-7">
            <div className="max-w-xl">
              <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-runcards-accent">
                The community
              </p>
              <h2 className="font-runcards-black text-[clamp(2rem,5vw,3.25rem)] leading-[1.0] tracking-[-0.025em] text-runcards-text-primary">
                {featuredClubs.length} {featuredClubs.length === 1 ? 'club' : 'clubs'}
                <span className="text-runcards-text-secondary">{cityLabel ? ` in ${cityLabel}` : ' nearby'}</span>
              </h2>
              <p className="mt-3 max-w-md font-runcards text-base text-runcards-text-secondary">
                Local run clubs with a place, a crew, and a standing invite.
              </p>
            </div>
          </div>
        </div>

        {homepageLoading ? (
          <div className="text-center py-12">
            <p className="text-runcards-text-secondary text-runcards-lg font-runcards">Loading clubs...</p>
          </div>
        ) : featuredClubs.length > 0 ? (
          <CardMarquee
            items={featuredClubs.slice(0, 18)}
            reverse
            renderItem={(club, index) => (
              <ClubCard
                club={club}
                priority={index === 0}
                useCardSize={true}
                onClick={() => handleCardOpen({ type: "club", id: club.id, label: club.name || "this club" })}
                onButtonClick={() => handleCardOpen({ type: "club", id: club.id, label: club.name || "this club" })}
              />
            )}
          />
        ) : (
          <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
            <div className="text-center py-16">
              <div className="bg-runcards-foreground/80 backdrop-blur-xl border border-runcards-border rounded-runcards-lg p-8 max-w-md mx-auto">
                <MapPin className="w-16 h-16 text-runcards-text-secondary-400 mx-auto mb-4" />
                <h3 className="text-runcards-xl font-runcards-bold text-runcards-text-primary mb-2">No clubs found in {searchCity ? searchCity.split(',')[0] : 'this area'}</h3>
                <p className="text-runcards-text-secondary-400 mb-6">
                  We don't have any running clubs in this area yet. Try searching for nearby cities or check out clubs in other locations.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="runcards-glass-dark"
                    size="runcards-medium"
                    onClick={() => navigate('/runclubs')}
                  >
                    Browse All Clubs
                  </Button>
                  <Button
                    variant="runcards-glass-dark"
                    size="runcards-medium"
                    onClick={() => {
                      const heroSection = document.querySelector('section.relative.min-h-screen');
                      if (heroSection) {
                        heroSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                  >
                    Search Different City
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Reveal>

      {/* How RunCards works — merged screenshot-paired feature section */}
      <Reveal>
        <HowItWorksSection />
      </Reveal>

      {/* "Inside the app" — scrolling marquee of real app screens */}
      <Reveal>
        <AppGallerySection />
      </Reveal>

      {/* For Run Clubs */}
      <Reveal>
        <ForRunClubsSection />
      </Reveal>

      {/* Registration Form Modal */}
      {showRegistrationForm && selectedRun && (
        <RunRegistrationForm
          meeting={selectedRun}
          onClose={() => {
            setShowRegistrationForm(false);
            setSelectedRun(null);
          }}
          onSuccess={() => {
            // Optionally refresh data or show success message
            setShowRegistrationForm(false);
            setSelectedRun(null);
          }}
        />
      )}

      {/* Meeting Modal */}
      {selectedRun && (
        <ScrollableCardModal
          cardType="meeting"
          cardId={selectedRun.id}
          isOpen={showRunModal}
          onClose={() => {
            setShowRunModal(false);
            setSelectedRun(null);
          }}
        />
      )}

      {/* Download-app interstitial — shown when a tapped run/club couldn't open
          the app (not installed / desktop). Carries the deep link so the user
          can retry opening the app. Full details live in the mobile app. */}
      <DownloadAppPrompt
        open={appPrompt.open}
        subjectLabel={appPrompt.label}
        deepLinkType={appPrompt.type}
        deepLinkId={appPrompt.id}
        onClose={closeAppPrompt}
      />
    </div>
  );
};

export default HomePage;